import React, { PureComponent, ReactNode } from 'react';
import Measure, { ContentRect } from 'react-measure';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList } from 'react-window';

interface Props {
	header: ReactNode;
	footer: ReactNode;
	itemsCount: number;
	item(index: number): ReactNode;
}

interface State {
	itemHeights: number[];
}

export default class List extends PureComponent<Props, State> {
	state = {
		itemHeights: [],
	};

	listRef: { current: VariableSizeList | null } = React.createRef();

	componentDidUpdate(prevProps: Props, prevState: State) {
		const list = this.listRef.current;
		if (!list) {
			return;
		}
		if (this.state.itemHeights !== prevState.itemHeights || prevProps.header !== this.props.header) {
			list.resetAfterIndex(0, true);
		} else if (prevProps.footer !== this.props.footer) {
			list.resetAfterIndex(this.props.itemsCount, true);
		}
	}

	render() {
		const { itemsCount } = this.props;

		return (
			<AutoSizer>
				{({ width, height }: { height: number; width: number }) => (
					<VariableSizeList
						estimatedItemSize={100}
						height={height}
						itemCount={itemsCount + 2}
						itemSize={this.resolveItemHeight}
						ref={this.listRef}
						width={width}
					>
						{this.renderItem}
					</VariableSizeList>
				)}
			</AutoSizer>
		);
	}

	resolveItemHeight = (index: number): number => {
		return this.state.itemHeights[index] ?? 100;
	};

	renderItem = ({ index, style }: { index: number; style: any }) => {
		return (
			<div style={style}>
				<Measure bounds onResize={(rect: ContentRect) => this.itemResized(index, rect)}>
					{({ measureRef }) => <div ref={measureRef}>{this.renderItemContent(index)}</div>}
				</Measure>
			</div>
		);
	};

	renderItemContent(index: number): ReactNode {
		const { header, footer, itemsCount, item } = this.props;

		switch (index) {
			case 0:
				return header;
			case itemsCount + 1:
				return footer;
			default:
				return item(index - 1);
		}
	}

	itemResized(index: number, rect: ContentRect) {
		this.setState((prevState) => {
			const itemHeights = [...prevState.itemHeights];
			itemHeights[index] = rect.bounds?.height || 0;
			return { ...prevState, itemHeights };
		});
	}
}
